/**
 * ============================================================================
 * TransitionDialog — Phase 6d frontend (Supplier Management).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: capture target lifecycle status + rationale + a single signer
 *          bundle id, and POST it. The server is the source of truth on
 *          legal arrows; we only offer reasonable targets for the current
 *          status.
 *
 * NOTE: Disqualification is NOT routed through this dialog — see
 *  DisqualifyDialog, which collects two signers and enforces a long
 *  rationale.
 * ============================================================================ */

import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@edms/design-system';
import dialogStyles from '../AuthorWorkbench/NewDocumentForm.module.css';
import styles from './SupplierDetail.module.css';
import { useTransitionMutation } from './useTransitionMutation';
import type { SupplierLifecycleStatus } from '../SupplierWorkbench/useSuppliersQuery';

/** Suggest a small target set per current status. Server still has final say. */
const NEXT_TARGETS: Record<SupplierLifecycleStatus, SupplierLifecycleStatus[]> = {
  PENDING_REQUEST: ['QUESTIONNAIRE_ISSUED', 'ARCHIVED'],
  QUESTIONNAIRE_ISSUED: ['AUDIT_SCHEDULED', 'ARCHIVED'],
  AUDIT_SCHEDULED: ['ACTIVE', 'RESTRICTED'],
  ACTIVE: ['RESTRICTED', 'ARCHIVED'],
  RESTRICTED: ['ACTIVE', 'ARCHIVED'],
  DISQUALIFIED: ['ARCHIVED'],
  ARCHIVED: [],
};

export interface TransitionDialogProps {
  supplierId: string;
  currentStatus: SupplierLifecycleStatus;
  onClose: () => void;
  onTransitioned: () => void;
}

export function TransitionDialog({
  supplierId,
  currentStatus,
  onClose,
  onTransitioned,
}: TransitionDialogProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const descId = useId();
  const firstRef = useRef<HTMLSelectElement | null>(null);

  const transitionM = useTransitionMutation();
  const targets = NEXT_TARGETS[currentStatus] ?? [];
  const [target, setTarget] = useState<SupplierLifecycleStatus | ''>(
    targets[0] ?? '',
  );
  const [rationale, setRationale] = useState('');
  const [signature, setSignature] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    firstRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!target) {
      setError(
        t('supplier.detail.transition.errors.targetRequired', {
          defaultValue: 'Pick a target status.',
        }),
      );
      return;
    }
    if (!rationale.trim()) {
      setError(
        t('supplier.detail.transition.errors.rationaleRequired', {
          defaultValue: 'Rationale is required.',
        }),
      );
      return;
    }
    if (!signature.trim()) {
      setError(
        t('supplier.detail.transition.errors.signatureRequired', {
          defaultValue: 'A signature bundle id is required.',
        }),
      );
      return;
    }
    try {
      await transitionM.mutateAsync({
        supplierId,
        target_status: target,
        rationale: rationale.trim(),
        signature_bundle_id: signature.trim(),
      });
      onTransitioned();
    } catch (e: unknown) {
      setError(
        e instanceof Error
          ? e.message
          : t('supplier.detail.transition.errors.serverFailed', {
              defaultValue: 'Server rejected the transition.',
            }),
      );
    }
  }

  return (
    <div
      className={dialogStyles.backdrop}
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={dialogStyles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
      >
        <header className={dialogStyles.header}>
          <h2 id={titleId} className={dialogStyles.title}>
            {t('supplier.detail.transition.title', {
              defaultValue: 'Transition lifecycle status',
            })}
          </h2>
          <p id={descId} className={dialogStyles.desc}>
            {t('supplier.detail.transition.subtitle', {
              defaultValue:
                'Current status: {{current}}. The server validates the chosen target against the supplier-lifecycle state machine.',
              current: t(`supplier.lifecycle.${currentStatus}`, {
                defaultValue: currentStatus,
              }),
            })}
          </p>
        </header>

        <form className={dialogStyles.form} onSubmit={onSubmit} noValidate>
          {error && (
            <div role="alert" className={dialogStyles.errorBanner}>
              {error}
            </div>
          )}

          <div className={dialogStyles.field}>
            <label htmlFor="sup-trn-target" className={dialogStyles.label}>
              {t('supplier.detail.transition.target', {
                defaultValue: 'Target status',
              })}
              <span className={dialogStyles.required} aria-hidden="true">*</span>
            </label>
            <select
              id="sup-trn-target"
              ref={firstRef}
              className={dialogStyles.select}
              value={target}
              onChange={(e) =>
                setTarget(e.target.value as SupplierLifecycleStatus)
              }
            >
              {targets.length === 0 && (
                <option value="">
                  {t('supplier.detail.transition.noTargets', {
                    defaultValue: 'No legal targets from this status',
                  })}
                </option>
              )}
              {targets.map((s) => (
                <option key={s} value={s}>
                  {t(`supplier.lifecycle.${s}`, { defaultValue: s })}
                </option>
              ))}
            </select>
          </div>

          <div className={dialogStyles.field}>
            <label htmlFor="sup-trn-rationale" className={dialogStyles.label}>
              {t('supplier.detail.transition.rationale', {
                defaultValue: 'Rationale',
              })}
              <span className={dialogStyles.required} aria-hidden="true">*</span>
            </label>
            <textarea
              id="sup-trn-rationale"
              className={styles.dialogTextarea}
              value={rationale}
              rows={3}
              onChange={(e) => setRationale(e.target.value)}
              required
            />
          </div>

          <div className={dialogStyles.field}>
            <label htmlFor="sup-trn-sig" className={dialogStyles.label}>
              {t('supplier.detail.transition.signature', {
                defaultValue: 'Signature bundle id',
              })}
              <span className={dialogStyles.required} aria-hidden="true">*</span>
            </label>
            <input
              id="sup-trn-sig"
              className={dialogStyles.input}
              value={signature}
              onChange={(e) => setSignature(e.target.value)}
              placeholder="sig-…"
              required
            />
          </div>

          <footer className={dialogStyles.footer}>
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={transitionM.isPending}
            >
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button
              type="submit"
              variant="primary"
              isLoading={transitionM.isPending}
              disabled={targets.length === 0}
            >
              {t('supplier.detail.transition.submit', {
                defaultValue: 'Transition',
              })}
            </Button>
          </footer>
        </form>
      </div>
    </div>
  );
}
